import { APP_VERSION } from '@constants/appVersion';

export type TelemetryDeviceType = 'mobile' | 'tablet' | 'desktop';

export interface TelemetryEnvironmentPayload extends Record<string, string> {
  appVersion: string;
  platform: string;
  systemVersion: string;
  browser: string;
  browserName: string;
  browserVersion: string;
  device: TelemetryDeviceType;
  isMobile: string;
  isTablet: string;
  isDesktop: string;
  isTouchCapable: string;
  locale: string;
  screenResolutionWidth: string;
  screenResolutionHeight: string;
}

let cachedPayload: TelemetryEnvironmentPayload | null = null;

function detectBrowser(userAgent: string): { name: string; version: string } {
  if (/Edg\//.test(userAgent)) {
    const match = userAgent.match(/Edg\/([\d.]+)/);
    return { name: 'Edge', version: match?.[1] ?? 'unknown' };
  }
  if (/Firefox\//.test(userAgent)) {
    const match = userAgent.match(/Firefox\/([\d.]+)/);
    return { name: 'Firefox', version: match?.[1] ?? 'unknown' };
  }
  if (/Chrome\//.test(userAgent) && !/Chromium/.test(userAgent)) {
    const match = userAgent.match(/Chrome\/([\d.]+)/);
    return { name: 'Chrome', version: match?.[1] ?? 'unknown' };
  }
  if (/Safari\//.test(userAgent) && !/Chrome/.test(userAgent)) {
    const match = userAgent.match(/Version\/([\d.]+)/);
    return { name: 'Safari', version: match?.[1] ?? 'unknown' };
  }
  return { name: 'Other', version: 'unknown' };
}

function detectPlatform(userAgent: string): { platform: string; systemVersion: string } {
  if (/iPhone|iPad|iPod/.test(userAgent)) {
    const match = userAgent.match(/OS ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, '.') : 'unknown';
    return {
      platform: /iPad/.test(userAgent) ? 'iPadOS' : 'iOS',
      systemVersion: `iOS ${version}`,
    };
  }
  if (/Android/.test(userAgent)) {
    const match = userAgent.match(/Android ([\d.]+)/);
    return {
      platform: 'Android',
      systemVersion: `Android ${match?.[1] ?? 'unknown'}`,
    };
  }
  if (/Mac OS X/.test(userAgent)) {
    const match = userAgent.match(/Mac OS X ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, '.') : 'unknown';
    return { platform: 'macOS', systemVersion: `macOS ${version}` };
  }
  if (/Windows/.test(userAgent)) {
    const match = userAgent.match(/Windows NT ([\d.]+)/);
    return {
      platform: 'Windows',
      systemVersion: match ? `Windows NT ${match[1]}` : 'Windows',
    };
  }
  if (/Linux/.test(userAgent)) {
    return { platform: 'Linux', systemVersion: 'Linux' };
  }
  return { platform: 'unknown', systemVersion: 'unknown' };
}

export function detectDeviceType(userAgent: string): TelemetryDeviceType {
  const isTablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent);
  const isMobile = /Mobi|iPhone|iPod|Android.*Mobile/i.test(userAgent) && !isTablet;
  if (isTablet) {
    return 'tablet';
  }
  if (isMobile) {
    return 'mobile';
  }
  return 'desktop';
}

/** Reserved TelemetryDeck payload keys for Devices / Versions dashboards. */
export function getTelemetryEnvironmentPayload(): TelemetryEnvironmentPayload {
  if (cachedPayload) {
    return cachedPayload;
  }

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = detectBrowser(userAgent);
  const platform = detectPlatform(userAgent);
  const deviceType = detectDeviceType(userAgent);

  cachedPayload = {
    appVersion: APP_VERSION,
    platform: platform.platform,
    systemVersion: platform.systemVersion,
    browser: browser.name,
    browserName: browser.name,
    browserVersion: browser.version,
    device: deviceType,
    isMobile: String(deviceType === 'mobile'),
    isTablet: String(deviceType === 'tablet'),
    isDesktop: String(deviceType === 'desktop'),
    isTouchCapable: String(
      typeof navigator !== 'undefined' &&
        (navigator.maxTouchPoints > 0 || 'ontouchstart' in window)
    ),
    locale: typeof navigator !== 'undefined' ? navigator.language : 'unknown',
    screenResolutionWidth: String(
      typeof window !== 'undefined' ? (window.screen?.width ?? 0) : 0
    ),
    screenResolutionHeight: String(
      typeof window !== 'undefined' ? (window.screen?.height ?? 0) : 0
    ),
  };

  return cachedPayload;
}
